import { Button } from "@/components/ui/button";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { ChevronLeftIcon, ChevronRightIcon } from "lucide-react";

interface Props {
  currentPageNumber: number;
  numberOfObjectsPerPage: number;
  totalRecord: number;
  onPageChange: (newPageNumber: number) => void;
  isLoading?: boolean;
}

export const Pagination: React.FC<Props> = ({
  currentPageNumber,
  numberOfObjectsPerPage,
  totalRecord,
  onPageChange,
  isLoading,
}) => {
  const { t } = useTranslation();
  const prevPage = currentPageNumber - 1;
  const canGoToPrevPage = prevPage > 0;
  const canGoToNextPage = Number(totalRecord) > Number(numberOfObjectsPerPage * currentPageNumber);

  const totalPages = Math.ceil(totalRecord / numberOfObjectsPerPage);

  const getPageNumbers = () => {
    const delta = 2;
    const range: (number | string)[] = [];

    for (let i = 1; i <= totalPages; i++) {
      if (
        i === 1 || // First page
        i === totalPages || // Last page
        (i >= currentPageNumber - delta && i <= currentPageNumber + delta) // Pages around current
      ) {
        range.push(i);
      } else if (range[range.length - 1] !== "...") {
        range.push("...");
      }
    }

    return range;
  };

  return (
    <div className="flex items-center justify-between space-x-2 py-4">
      <div className="text-sm text-muted-foreground animate-in fade-in-50 duration-300">
        {t("dataTable.pagination.total", { total: totalRecord })}
      </div>
      <div className="flex items-center space-x-2 animate-in slide-in-from-right-5 duration-500">
        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPageNumber - 1)}
          disabled={!canGoToPrevPage || isLoading}
          className={cn(
            "h-8 w-8 p-0 transition-all duration-200",
            "hover:bg-accent/20 hover:text-accent-foreground",
            "focus:ring-2 focus:ring-primary/20"
          )}
        >
          <ChevronLeftIcon className="h-4 w-4" />
        </Button>

        {/* Page numbers */}
        <div className="flex items-center space-x-1">
          {getPageNumbers().map((pageNum, idx) =>
            pageNum === "..." ? (
              <span key={`ellipsis-${idx}`} className="px-2 text-muted-foreground">
                •••
              </span>
            ) : (
              <Button
                key={pageNum}
                variant={currentPageNumber === pageNum ? "default" : "outline"}
                size="sm"
                onClick={() => onPageChange(Number(pageNum))}
                disabled={isLoading}
                className={cn(
                  "h-8 w-8 p-0 transition-all duration-200",
                  currentPageNumber === pageNum
                    ? ["bg-primary text-primary-foreground hover:bg-primary/90", "shadow-sm hover:shadow"]
                    : ["hover:bg-accent/20 hover:text-accent-foreground", "focus:ring-2 focus:ring-primary/20"]
                )}
              >
                {pageNum}
              </Button>
            )
          )}
        </div>

        <Button
          variant="outline"
          size="sm"
          onClick={() => onPageChange(currentPageNumber + 1)}
          disabled={!canGoToNextPage || isLoading}
          className={cn(
            "h-8 w-8 p-0 transition-all duration-200",
            "hover:bg-accent/20 hover:text-accent-foreground",
            "focus:ring-2 focus:ring-primary/20"
          )}
        >
          <ChevronRightIcon className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
};
